import React, { useState } from 'react';
import { Download, Eye, FileText } from 'lucide-react';

/**
 * WindsorPanel — Due Diligence Report Display & Download
 * Displays the Windsor property acquisition report with download options
 */
export default function WindsorPanel() {
  const [showPreview, setShowPreview] = useState(false);
  const reportUrl = '/api/reports/windsor'; // Serves from backend
  const htmlUrl = 'http://localhost:18888/Windsor_Due_Diligence_Report.html'; // Local access

  const handleDownloadHTML = () => {
    // Download the HTML file
    fetch('/api/reports/windsor/html')
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Windsor_Due_Diligence_Report_16_Duchess_Avenue.html';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      })
      .catch(err => alert('Error downloading file: ' + err.message));
  };

  const handleDownloadPDF = () => {
    // Try to generate PDF from server-side
    fetch('/api/reports/windsor/pdf')
      .then(res => {
        if (!res.ok) throw new Error('PDF generation failed');
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Windsor_Due_Diligence_Report_16_Duchess_Avenue.pdf';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      })
      .catch(err => {
        alert('PDF download failed. Please download HTML version and print to PDF from your browser.');
        handleDownloadHTML();
      });
  };

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
        <h1 className="text-2xl font-bold mb-2">Windsor Due Diligence Report</h1>
        <p className="text-red-100">Erf 494, 16 Duchess Avenue, Windsor West, Randburg</p>
        <p className="text-red-100 text-sm mt-1">Generated: May 20, 2026 | LightStone Valuation + Independent Analysis</p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">R 2.89M</div>
              <div className="text-xs text-gray-600 mt-1">Municipal Valuation</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">R 2.28M</div>
              <div className="text-xs text-gray-600 mt-1">LightStone Expected</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">R 2.91M</div>
              <div className="text-xs text-gray-600 mt-1">Market Average (20 comparables)</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">991 m²</div>
              <div className="text-xs text-gray-600 mt-1">Erf Size</div>
            </div>
          </div>

          {/* Report Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-bold text-blue-900 mb-3">📊 Report Contents</h2>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>✓ Official LightStone Erf Valuation (R 2.28M expected)</li>
              <li>✓ 20 Comparable Sales Analysis (verified transactions)</li>
              <li>✓ Valuation Discrepancy Analysis (R 614K gap reconciliation)</li>
              <li>✓ Market Risk Assessment & Trends</li>
              <li>✓ Safety Assessment (SAPS/Stats SA data, 73% safety score)</li>
              <li>✓ Demographic Profile (Windsor West premium segment)</li>
              <li>✓ Neighborhood Amenities & Infrastructure</li>
              <li>✓ Critical Information Gaps & Required Due Diligence</li>
              <li>✓ Acquisition Strategy Framework & Financial Projections</li>
              <li>✓ Professional Valuation Recommendations</li>
            </ul>
          </div>

          {/* Download Section */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-bold text-red-900 mb-4">📥 Download Report</h2>
            <div className="space-y-3">
              <button
                onClick={handleDownloadPDF}
                className="w-full flex items-center justify-between bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <FileText size={18} />
                  Download as PDF
                </span>
                <Download size={18} />
              </button>
              <button
                onClick={handleDownloadHTML}
                className="w-full flex items-center justify-between bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <FileText size={18} />
                  Download as HTML (Interactive)
                </span>
                <Download size={18} />
              </button>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="w-full flex items-center justify-between bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Eye size={18} />
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </span>
              </button>
            </div>
          </div>

          {/* Key Findings */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-bold text-yellow-900 mb-3">⚠️ Key Findings</h2>
            <div className="space-y-3 text-sm text-yellow-800">
              <p>
                <strong>Valuation Gap:</strong> R 614K difference between LightStone automated (R 2.28M) and market comparables average (R 2.91M).
                Professional independent valuation recommended before offer.
              </p>
              <p>
                <strong>Critical Issue:</strong> Nedbank bond R 4M outstanding (2007). Redemption terms and settlement costs must be verified with lender.
              </p>
              <p>
                <strong>Fair Value Range:</strong> R 2.3M - R 2.9M. Conservative offer R 2.3-2.4M | Fair market R 2.6-2.75M | Maximum R 2.85-2.95M.
              </p>
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <iframe
                src={htmlUrl}
                title="Windsor Due Diligence Report Preview"
                className="w-full h-[800px] border-0"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
